import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { Button } from '../ui/button';
import { Check, Sparkles, Plus, CreditCard, Calculator, FileText } from 'lucide-react';
import './Pricing.css';

const Pricing = ({ onOpenConsultation }) => {
  const { pricing } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  // Translation map for pricing features and texts
  const translateText = (text) => {
    if (language === 'de') return text;
    const translations = {
      // Plan names
      'Augenbrauentransplantation': 'Eyebrow Transplant',
      'Barthaartransplantation': 'Beard Transplant',
      // Subtitles
      'FUE Saphir & DHI kombiniert je nach Bedarf': 'FUE Sapphire & DHI combined as needed',
      // Features
      'Kombinierte FUE Saphir & DHI Technik': 'Combined FUE Sapphire & DHI technique',
      'Lokale Betäubung': 'Local anesthesia',
      'Schmerzfreie Betäubung': 'Pain-free anesthesia',
      'Individuelle Augenbrauenformgestaltung': 'Individual eyebrow shaping',
      'Individuelle Bartformgestaltung': 'Individual beard shaping',
      'Nachsorgekit inklusive': 'Aftercare kit included',
      '12 Monate Nachbetreuung': '12 months follow-up care',
      // Grafts
      'bis 300 Grafts': 'up to 300 Grafts',
      'bis 800 Grafts': 'up to 800 Grafts',
      'bis 2.000 Grafts': 'up to 2,000 Grafts',
      'bis 4.500 Grafts': 'up to 4,500 Grafts',
    };
    return translations[text] || text;
  };

  // Check if this is eyebrow or beard pricing (single card with simple prices)
  const isEyebrowPricing = pricing.length === 1 && pricing[0].isEyebrowPricing;
  const isBeardPricing = pricing.length === 1 && pricing[0].isBeardPricing;
  
  // Render special eyebrow or beard pricing
  if (isEyebrowPricing || isBeardPricing) {
    const plan = pricing[0];
    const isSimple = plan.isSimplePricing;
    const pageType = isBeardPricing ? 'beard' : 'eyebrow';
    
    return (
      <section id="preise" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
        isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
      }`}>
        {/* Background */}
        <div className="absolute inset-0">
          <div className={`absolute top-0 left-0 w-[500px] h-[500px] rounded-full blur-[80px] ${
            isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
          }`} />
          <div className={`absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full blur-[80px] ${
            isDark ? 'bg-[#5A8BA6]/5' : 'bg-[#7BA7C2]/5'
          }`} />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Section Header */}
          <div className="text-center mb-12">
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
              isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
            }`}>
              {t('pricing.sectionBadge')}
            </span>
            <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {language === 'de' ? 'Unsere ' : 'Our '}
              <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
                {language === 'de' ? 'Preise' : 'Prices'}
              </span>
            </h2>
            <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {language === 'de' 
                ? (pageType === 'beard' ? 'Transparente Preise für Ihre Barthaartransplantation' : 'Transparente Preise für Ihre Augenbrauentransplantation')
                : (pageType === 'beard' ? 'Transparent prices for your beard transplantation' : 'Transparent prices for your eyebrow transplantation')}
            </p>
          </div>

          {/* Single Eyebrow Pricing Card */}
          <div className="relative">
            {/* Animated Border */}
            <div className="animated-border-box-glow" />
            <div className="animated-border-box" />
            
            <div className={`relative z-[2] rounded-2xl ${
              isDark ? 'bg-[#1a1a1a]' : 'bg-white'
            }`}>
              <div className="p-8">
                {/* Header */}
                <div className="text-center mb-8">
                  <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {translateText(plan.name)}
                  </h3>
                  {plan.subtitle && (
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {translateText(plan.subtitle)}
                    </p>
                  )}
                </div>

                {/* Simple Price Display */}
                {isSimple ? (
                  <div className="space-y-4 mb-8">
                    {plan.tiers.map((tier, index) => (
                      <div key={index} className={`flex items-center justify-between p-5 rounded-xl border ${
                        isDark ? 'bg-white/[0.03] border-white/10' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <span className={`text-base font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {translateText(tier.grafts)}
                        </span>
                        <span className={`text-2xl font-bold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                          {language === 'de' ? 'ab ' : 'from '}{tier.price}€
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Price Table for FUE/DHI comparison */
                  <div className="mb-8">
                    <div className={`grid grid-cols-3 gap-4 pb-3 mb-3 border-b ${
                      isDark ? 'border-white/10' : 'border-gray-200'
                    }`}>
                      <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {language === 'de' ? 'Umfang' : 'Scope'}
                      </div>
                      <div className={`text-sm font-medium text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        FUE Saphir
                      </div>
                      <div className={`text-sm font-medium text-center ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                        DHI {language === 'de' ? 'Methode' : 'Method'}
                      </div>
                    </div>
                    {plan.tiers.map((tier, index) => (
                      <div key={index} className={`grid grid-cols-3 gap-4 py-4 ${
                        index < plan.tiers.length - 1 ? (isDark ? 'border-b border-white/5' : 'border-b border-gray-100') : ''
                      }`}>
                        <div className={`flex items-center text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {tier.grafts}
                        </div>
                        <div className={`text-center`}>
                          <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {language === 'de' ? 'ab ' : 'from '}{tier.price}€
                          </span>
                        </div>
                        <div className={`text-center`}>
                          <span className={`text-xl font-bold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                            {language === 'de' ? 'ab ' : 'from '}{tier.dhiPrice}€
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Technique Info */}
                <div className={`flex items-center gap-2 p-4 rounded-xl mb-8 ${
                  isDark ? 'bg-[#7BA7C2]/10 border border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10 border border-[#5A8BA6]/20'
                }`}>
                  <Sparkles className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {pageType === 'eyebrow'
                      ? (language === 'de'
                        ? 'Die Haare werden in voller Länge entnommen und eingesetzt — ganz ohne Rasur, mit sofort sichtbarem Ergebnis'
                        : 'Hairs are extracted and implanted at full length — no shaving at all, with an immediately visible result')
                      : (language === 'de'
                        ? 'Wir kombinieren FUE Saphir & DHI je nach Ihren individuellen Bedürfnissen für optimale Ergebnisse'
                        : 'We combine FUE Sapphire & DHI according to your individual needs for optimal results')}
                  </span>
                </div>

                {/* Features */}
                <div className="mb-8">
                  <h4 className={`text-sm font-semibold mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {language === 'de' ? 'Inklusive Leistungen:' : 'Included services:'}
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/10'
                        }`}>
                          <Check className={`w-3 h-3 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                        </div>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{translateText(feature)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Ratenzahlung */}
                <div className={`rounded-xl border-2 p-5 mb-8 ${
                  isDark ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-emerald-400/30 bg-emerald-50/60'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <h4 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {language === 'de' ? 'Bequeme Ratenzahlung möglich' : 'Flexible Installment Payments'}
                    </h4>
                  </div>
                  <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {language === 'de'
                      ? `Finanzieren Sie Ihre Behandlung einfach und flexibel — ab ${plan.monthlyFrom || '53'}€ im Monat. Die ersten 6 Monate sind komplett zinsfrei.`
                      : `Finance your treatment easily and flexibly — from ${plan.monthlyFrom || '53'}€ per month. The first 6 months are completely interest-free.`}
                  </p>
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500 text-white">
                      {language === 'de' ? '6 Monate zinsfrei' : '6 months interest-free'}
                    </span>
                    <span className={`text-sm font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                      {language === 'de' ? `ab ${plan.monthlyFrom || '53'}€/Monat` : `from ${plan.monthlyFrom || '53'}€/month`}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://www.medipay.de/ratengenerator/index.php?i=500"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 ${
                        isDark ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 hover:bg-emerald-500/25' : 'bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-200'
                      }`}
                    >
                      <Calculator className="w-4 h-4" />
                      {language === 'de' ? 'Ratenrechner' : 'Rate Calculator'}
                    </a>
                    <a
                      href="/docs/Ratenzahlung.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 ${
                        isDark ? 'bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      {language === 'de' ? 'Infos zur Ratenzahlung' : 'Financing Information'}
                    </a>
                  </div>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={onOpenConsultation}
                  className="w-full py-6 text-lg bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white shadow-lg shadow-[#7BA7C2]/25"
                >
                  {language === 'de' ? 'Kostenlose Beratung anfordern' : 'Request Free Consultation'}
                </Button>
              </div>
            </div>
          </div>

          {/* Note */}
          <p className={`text-center text-sm mt-8 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {language === 'de' 
              ? '* Alle Preise verstehen sich als Richtwerte. Der genaue Preis wird nach einer individuellen Analyse festgelegt.'
              : '* All prices are guideline values. The exact price will be determined after an individual analysis.'}
          </p>
        </div>
      </section>
    );
  }

  // Check if this is DHI pricing (3 packages with fixed prices)
  const isDHIPricing = pricing.length > 0 && pricing[0].isDHIPricing;
  
  if (isDHIPricing) {
    return (
      <section id="preise" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
        isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
      }`}>
        {/* Background */}
        <div className="absolute inset-0">
          <div className={`absolute top-0 left-0 w-[500px] h-[500px] rounded-full blur-[80px] ${
            isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
          }`} />
          <div className={`absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full blur-[80px] ${
            isDark ? 'bg-[#5A8BA6]/5' : 'bg-[#7BA7C2]/5'
          }`} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Section Header */}
          <div className="text-center mb-12">
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
              isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
            }`}>
              {language === 'de' ? 'DHI Pakete' : 'DHI Packages'}
            </span>
            <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {language === 'de' ? 'DHI ohne Rasur ' : 'DHI without Shaving '}
              <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
                {language === 'de' ? 'Preise' : 'Prices'}
              </span>
            </h2>
            <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {language === 'de' 
                ? 'Transparente Festpreise für Ihre diskrete DHI-Haartransplantation ohne Rasur'
                : 'Transparent fixed prices for your discreet DHI hair transplant without shaving'}
            </p>
          </div>

          {/* DHI Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {pricing.map((plan) => (
              <div 
                key={plan.id}
                className={`relative rounded-2xl overflow-hidden transition-all duration-500 ${
                  plan.popular 
                    ? 'transform md:-translate-y-4' 
                    : ''
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 z-10">
                    <div className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-center py-2 text-sm font-medium">
                      {language === 'de' ? 'Beliebteste Wahl' : 'Most Popular'}
                    </div>
                  </div>
                )}

                {/* Card */}
                <div className={`h-full flex flex-col border ${
                  plan.popular
                    ? isDark 
                      ? 'bg-[#1a1a1a] border-[#7BA7C2]/50 shadow-xl shadow-[#7BA7C2]/10' 
                      : 'bg-white border-[#5A8BA6]/50 shadow-xl shadow-[#5A8BA6]/10'
                    : isDark
                      ? 'bg-[#141414] border-white/10'
                      : 'bg-white border-gray-200'
                } rounded-2xl ${plan.popular ? 'pt-10' : ''}`}>
                  
                  <div className="p-6 lg:p-8 flex flex-col flex-grow">
                    {/* Header */}
                    <div className="text-center mb-6">
                      <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {plan.name}
                      </h3>
                      {plan.subtitle && (
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {plan.subtitle}
                        </p>
                      )}
                    </div>

                    {/* Price */}
                    <div className="text-center mb-6">
                      <div className={`flex items-baseline justify-center gap-1`}>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'de' ? 'ab' : 'from'}
                        </span>
                        <span className={`text-4xl font-bold ${
                          plan.popular 
                            ? isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'
                            : isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {plan.price}€
                        </span>
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-3 mb-8 flex-grow">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/10'
                          }`}>
                            <Check className={`w-3 h-3 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                          </div>
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <Button
                      onClick={onOpenConsultation}
                      className={`w-full py-5 ${
                        plan.popular
                          ? 'bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white shadow-lg shadow-[#7BA7C2]/25'
                          : isDark
                            ? 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                      }`}
                    >
                      {language === 'de' ? 'Jetzt anfragen' : 'Inquire Now'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Note */}
          <p className={`text-center text-sm mt-10 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {language === 'de' 
              ? '* Alle Preise sind Festpreise. Der genaue Umfang wird nach einer individuellen Analyse festgelegt.'
              : '* All prices are fixed prices. The exact scope will be determined after an individual analysis.'}
          </p>
        </div>
      </section>
    );
  }
  
  // Single plan layout (Haartransplantation with one package)
  const isSinglePlan = pricing.length === 1 && pricing[0].isSinglePlan;

  if (isSinglePlan) {
    const plan = pricing[0];

    const featureTranslations = {
      'FUE Saphir Methode': 'FUE Sapphire Method',
      'Lokale Betäubung': 'Local Anesthesia',
      'Schmerzfreie Betäubung': 'Pain-free Anesthesia',
      'Nachsorgekit (Nackenkissen, Shampoo) + Medikamentenrezept': 'Aftercare kit (neck pillow, shampoo) + medication prescription',
      '12 Monate Nachbetreuung': '12 months aftercare',
      '2 Kostenlose Regenerative Haartherapien': '2 Free regenerative hair therapies',
    };

    return (
      <section id="preise" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
        isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
      }`}>
        <div className="absolute inset-0">
          <div className={`absolute top-0 left-0 w-[500px] h-[500px] rounded-full blur-[80px] ${
            isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
          }`} />
          <div className={`absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full blur-[80px] ${
            isDark ? 'bg-[#5A8BA6]/5' : 'bg-[#7BA7C2]/5'
          }`} />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-14">
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
              isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
            }`}>
              {t('pricing.sectionBadge')}
            </span>
            <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {language === 'de' ? 'Unsere ' : 'Our '}
              <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
                {language === 'de' ? 'Preise' : 'Prices'}
              </span>
            </h2>
            <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {language === 'de'
                ? 'Transparente Festpreise für Ihre Haartransplantation'
                : 'Transparent fixed prices for your hair transplant'}
            </p>
          </div>

          <div className={`rounded-2xl border overflow-hidden ${
            isDark ? 'bg-[#1a1a1a] border-white/5' : 'bg-white border-gray-200 shadow-lg'
          }`}>
            <div className="p-6 sm:p-8 lg:p-10">
              <div className="text-center mb-8">
                <h3 className={`text-2xl sm:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'de' ? plan.name : 'Hair Transplant'}
                </h3>
                {plan.subtitle && (
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'de' ? plan.subtitle : 'FUE Sapphire & DHI combined as needed'}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {plan.tiers.map((tier, i) => {
                  const translatedGrafts = tier.grafts.includes('2.000')
                    ? (language === 'de' ? 'bis 2.000 Grafts' : 'up to 2,000 Grafts')
                    : (language === 'de' ? 'bis 4.500 Grafts' : 'up to 4,500 Grafts');
                  return (
                    <div key={i} className={`p-5 rounded-xl border text-center ${
                      isDark ? 'bg-white/[0.03] border-white/8' : 'bg-gray-50 border-gray-100'
                    }`}>
                      <div className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{translatedGrafts}</div>
                      <div className={`text-3xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {language === 'de' ? 'ab' : 'from'} {tier.price}€
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={`flex items-center justify-center gap-2 p-4 rounded-xl border mb-3 ${
                isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/8 border-[#5A8BA6]/15'
              }`}>
                <Plus className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {language === 'de' ? 'Ohne Rasur Oberkopf (Saphir-DHI Hybrid)' : 'No Shaving Top (Sapphire-DHI Hybrid)'}
                </span>
                <span className={`font-bold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>+{plan.hybridPrice}€</span>
              </div>

              {plan.longHairPrice && (
                <div className={`flex items-center justify-center gap-2 p-4 rounded-xl border mb-8 ${
                  isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200/50'
                }`}>
                  <Plus className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {language === 'de' ? 'Long Hair FUE (komplett ohne Rasur)' : 'Long Hair FUE (completely no shave)'}
                  </span>
                  <span className={`font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>+{plan.longHairPrice}€</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {plan.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/10'
                    }`}>
                      <Check className={`w-3 h-3 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                    </div>
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'en' ? (featureTranslations[feature] || feature) : feature}
                    </span>
                  </div>
                ))}
              </div>

              {/* Ratenzahlung */}
              <div className={`rounded-xl border-2 p-5 sm:p-6 mb-8 ${
                isDark ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-emerald-400/30 bg-emerald-50/60'
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <h4 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {language === 'de' ? 'Bequeme Ratenzahlung möglich' : 'Flexible Installment Payments'}
                  </h4>
                </div>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {language === 'de'
                    ? `Finanzieren Sie Ihre Behandlung einfach und flexibel — ab ${plan.monthlyFrom || '53'}€ im Monat. Die ersten 6 Monate sind komplett zinsfrei.`
                    : `Finance your treatment easily and flexibly — from ${plan.monthlyFrom || '53'}€ per month. The first 6 months are completely interest-free.`}
                </p>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500 text-white">
                    {language === 'de' ? '6 Monate zinsfrei' : '6 months interest-free'}
                  </span>
                  <span className={`text-sm font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                    {language === 'de' ? `ab ${plan.monthlyFrom || '53'}€/Monat` : `from ${plan.monthlyFrom || '53'}€/month`}
                  </span>
                </div>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://www.medipay.de/ratengenerator/index.php?i=500"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 ${
                      isDark ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 hover:bg-emerald-500/25' : 'bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-200'
                    }`}
                    data-testid="financing-calculator-link"
                  >
                    <Calculator className="w-4 h-4" />
                    {language === 'de' ? 'Ratenrechner' : 'Rate Calculator'}
                  </a>
                  <a
                    href="/docs/Ratenzahlung.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 ${
                      isDark ? 'bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    }`}
                    data-testid="financing-info-link"
                  >
                    <FileText className="w-4 h-4" />
                    {language === 'de' ? 'Infos zur Ratenzahlung' : 'Financing Information'}
                  </a>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={onOpenConsultation}
                  className="flex-1 py-6 text-lg bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white shadow-lg shadow-[#7BA7C2]/25"
                >
                  {language === 'de' ? 'Kostenlose Beratung anfordern' : 'Request Free Consultation'}
                </Button>
                <a
                  href="/preisrechner"
                  className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-all ${
                    isDark ? 'bg-white/5 hover:bg-white/10 text-white border border-white/10' : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-200'
                  }`}
                  data-testid="pricing-calculator-cta"
                >
                  {language === 'de' ? 'Kosten berechnen' : 'Calculate Costs'}
                  <span className="text-xs opacity-60">→</span>
                </a>
              </div>
            </div>
          </div>

          <p className={`text-center text-sm mt-10 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {language === 'de'
              ? '* Alle Preise sind Festpreise. Der genaue Umfang wird nach einer individuellen Analyse festgelegt.'
              : '* All prices are fixed prices. The exact scope will be determined after an individual analysis.'}
          </p>
        </div>
      </section>
    );
  }

  // Default multi-card layout (fallback)
  return (
    <section id="preise" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
    }`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-0 left-0 w-[500px] h-[500px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
        <div className={`absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#5A8BA6]/5' : 'bg-[#7BA7C2]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            {t('pricing.sectionBadge')}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Unsere ' : 'Our '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Preise' : 'Prices'}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('pricing.sectionDescription')}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {pricing.map((plan) => (
            <div
              key={plan.id}
              className={`group relative transition-all duration-500 flex card-lift ${
                plan.popular ? 'z-10 md:-mt-8' : ''
              }`}
            >
              {/* Animated Border for Popular Card */}
              {plan.popular && (
                <>
                  {/* Glow Layer */}
                  <div className="animated-border-box-glow" />
                  {/* Border Layer */}
                  <div className="animated-border-box" />
                </>
              )}

              {/* Card Content Container */}
              <div
                className={`relative rounded-2xl flex flex-col w-full min-h-0 md:min-h-[640px] ${
                  plan.popular
                    ? isDark
                      ? 'z-[2] bg-[#1a1a1a]'
                      : 'z-[2] bg-white'
                    : isDark 
                      ? 'bg-[#1a1a1a] border border-white/5 hover:border-[#7BA7C2]/30'
                      : 'bg-white border border-gray-200 shadow-sm hover:border-[#5A8BA6]/30'
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-sm font-medium shadow-lg shadow-[#7BA7C2]/40">
                      <Sparkles className="w-4 h-4" />
                      {language === 'de' ? 'Beliebteste Wahl' : 'Most Popular'}
                    </div>
                  </div>
                )}

                <div className="p-6 sm:p-8 flex flex-col h-full">
                  {/* Plan Name */}
                  <h3 className={`text-2xl font-bold mb-6 transition-colors duration-300 ${
                    isDark 
                      ? 'text-white group-hover:text-[#7BA7C2]' 
                      : 'text-gray-900 group-hover:text-[#5A8BA6]'
                  }`}>{plan.name}</h3>

                  {/* Price Tiers */}
                  <div className="space-y-3 mb-6">
                    {plan.tiers.map((tier, index) => {
                      // Translate graft tiers
                      const translatedGrafts = tier.grafts.includes('2.000') 
                        ? (language === 'de' ? 'bis 2.000 Grafts' : 'up to 2,000 Grafts')
                        : (language === 'de' ? 'bis 4.500 Grafts' : 'up to 4,500 Grafts');
                      
                      return (
                        <div key={index} className={`flex items-center justify-between p-3 rounded-xl border ${
                          isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border-gray-100'
                        }`}>
                          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{translatedGrafts}</span>
                          <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'de' ? 'ab' : 'from'} {tier.price}€</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Hybrid Option */}
                  <div className={`flex items-center gap-2 p-3 rounded-xl border mb-6 ${
                    isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20'
                  }`}>
                    <Plus className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'de' ? 'Ohne Rasur (Saphir-DHI Hybrid)' : 'No Shaving (Sapphire-DHI Hybrid)'}
                    </span>
                    <span className={`ml-auto font-semibold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>+{plan.hybridPrice}€</span>
                  </div>

                  {plan.longHairPrice && (
                    <div className={`flex items-center gap-3 p-3 rounded-xl border mb-6 ${
                      isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200/50'
                    }`}>
                      <Plus className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {language === 'de' ? 'Long Hair FUE (komplett ohne Rasur)' : 'Long Hair FUE (completely no shave)'}
                      </span>
                      <span className={`ml-auto font-semibold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>+{plan.longHairPrice}€</span>
                    </div>
                  )}

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature, index) => {
                      // Translate pricing features
                      const featureTranslations = {
                        'FUE Saphir Methode': language === 'de' ? 'FUE Saphir Methode' : 'FUE Sapphire Method',
                        'Lokale Betäubung': language === 'de' ? 'Lokale Betäubung' : 'Local Anesthesia',
                        'Schmerzfreie Betäubung': language === 'de' ? 'Schmerzfreie Betäubung' : 'Pain-free Anesthesia',
                        'Nachsorgekit (Nackenkissen, Shampoo) + Medikamentenrezept': language === 'de' ? 'Nachsorgekit (Nackenkissen, Shampoo) + Medikamentenrezept' : 'Aftercare kit (neck pillow, shampoo) + medication prescription',
                        'Nachsorgekit (Nackenkissen, Shampoo, Pflegespray und Medikamente)': language === 'de' ? 'Nachsorgekit (Nackenkissen, Shampoo, Pflegespray und Medikamente)' : 'Aftercare kit (neck pillow, shampoo, care spray and medication)',
                        '6 Monate Nachbetreuung': language === 'de' ? '6 Monate Nachbetreuung' : '6 months aftercare',
                        '12 Monate Nachbetreuung': language === 'de' ? '12 Monate Nachbetreuung' : '12 months aftercare',
                        '18 Monate Nachbetreuung': language === 'de' ? '18 Monate Nachbetreuung' : '18 months aftercare',
                        '1 Regenerative Haartherapie': language === 'de' ? '1 Regenerative Haartherapie' : '1 Regenerative hair therapy',
                        '3 Regenerative Haartherapien': language === 'de' ? '3 Regenerative Haartherapien' : '3 Regenerative hair therapies',
                        '5 Regenerative Haartherapien': language === 'de' ? '5 Regenerative Haartherapien' : '5 Regenerative hair therapies',
                        'Hotelübernachtung inklusive': language === 'de' ? 'Hotelübernachtung inklusive' : 'Hotel overnight stay included',
                      };
                      const translatedFeature = featureTranslations[feature] || feature;
                      
                      return (
                        <li key={index} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/10'
                          }`}>
                            <Check className={`w-3 h-3 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                          </div>
                          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{translatedFeature}</span>
                        </li>
                      );
                    })}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    onClick={onOpenConsultation}
                    className={`w-full py-6 text-lg transition-all duration-300 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white shadow-lg shadow-[#7BA7C2]/25'
                        : isDark 
                          ? 'bg-white/5 hover:bg-[#7BA7C2]/20 text-white border border-white/10 hover:border-[#7BA7C2]/30'
                          : 'bg-gray-100 hover:bg-[#5A8BA6]/10 text-gray-900 border border-gray-200 hover:border-[#5A8BA6]/30'
                    }`}
                  >
                    {language === 'de' ? 'Beratung anfordern' : 'Request Consultation'}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <p className={`text-center text-sm mt-12 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          {language === 'de' 
            ? '* Alle Preise verstehen sich als Richtwerte. Der genaue Preis wird nach einer individuellen Analyse festgelegt.'
            : '* All prices are guideline values. The exact price will be determined after an individual analysis.'}
        </p>

        {/* Price Calculator CTA */}
        <div className="text-center mt-8">
          <a
            href="/preisrechner"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 bg-gradient-to-r from-[#7BA7C2]/10 to-[#5A8BA6]/10 border border-[#7BA7C2]/20 hover:border-[#7BA7C2]/40 text-[#7BA7C2]"
            data-testid="pricing-calculator-cta"
          >
            {language === 'de' ? 'Individuelle Kosten berechnen' : 'Calculate your individual cost'}
            <span className="text-xs opacity-70">→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
